<template>
  <div v-if="subscribers">
    <div>
      <b-button variant="link" class="text-secondary text-medium-1 p-0" :id="'link-' + componentId">
        <span v-if="subscribers.length === 0">No Subscribers</span>
        <span v-else>
          <span>{{ subscribers.length }}</span>
          Subscriber<span v-if="subscribers.length > 1">s</span>
        </span>
      </b-button>
    </div>
    <b-popover triggers="hover" :target="'link-' + componentId" placement="bottom">
      <div class="subscribers-container text-medium-1">
        <h2 class="bold mb-4 text-medium-1" v-if="subscribers.length > 0">
          These people will be notified
        </h2>
        <div class="mb-1" :key="userId" v-for="userId in subscribers">
          <user-short
            class="mb-2 text-medium-1"
            :length="24"
            :user-id="userId"
            :show-user-name="true"
            :key="userId"
          />
        </div>
        <div v-if="subscribers.length > 0" class="mb-3"></div>
        <div>
          <div v-if="isUserSubscribed">
            <h6 class="text-medium-1">You are subscribed</h6>
            <div class="text-secondary">
              You’ll get a notification when comments are posted.
            </div>
          </div>
          <div v-else>
            <h6 class="text-medium-1">You’re not subscribed</h6>
            <div class="text-secondary">
              You won’t be notified when comments are posted.
            </div>
          </div>
          <b-button
            @click="toggleSubscription()"
            class="subscribe-btn text-medium-1 mt-4"
            variant="outline-secondary"
            pill
          >
            <b-spinner variant="secondary" small v-if="isSubscribing"></b-spinner>
            <span v-else>{{ isUserSubscribed ? 'Unsubscribe me' : 'Subscribe me' }}</span>
          </b-button>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
import UserShort from '@/components/UserShort'
import { ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import EventBusMixin from '@/mixins/EventBusMixin'
import service from '@/service'
import urlManager from '@/urlManager'
import random from '@/utils/random'
import showAPIError from '@/utils/showAPIError'
import { BPopover, BButton, BSpinner } from 'bootstrap-vue'
import * as R from 'ramda'

/**
 * This components shows the list of subscribers
 * This is being used at the place where comment is created(for goal/initiative). We need to show the list of users who will receive email notifications
 * @eventbus emits
 * - SUBSCRIBE/UNSUBSCRIBE
 *  payload: {entityType, entityId, userId}
 */
export default {
  name: 'Subscribers',
  props: ['orgId', 'entityType', 'entity'],
  components: {
    BPopover,
    BButton,
    UserShort,
    BSpinner
  },
  mixins: [EventBusMixin],
  data() {
    return {
      isSubscribing: false,
      subscribers: null,
      componentId: random(5)
    }
  },
  computed: {
    user() {
      return this.$store.getters.user
    },
    isUserSubscribed() {
      return this.subscribers?.includes(this.user.id)
    },
    entityId() {
      return this.entity?.id
    },
    entityMap() {
      let orgId = this.orgId
      return {
        [ENTITY_TYPE.GOAL]: {
          subscriptionsUrl: urlManager.goalSubscribers({ orgId, goalId: this.entity.id }),
          detailsUrl: urlManager.goalDetail({ orgId, goalId: this.entityId })
        },
        [ENTITY_TYPE.KEY_RESULT]: {
          subscriptionsUrl: urlManager.krSubscribers({ orgId, krId: this.entity.id }),
          detailsUrl: urlManager.KRDetail({ orgId, krId: this.entityId })
        },
        [ENTITY_TYPE.INITIATIVE]: {
          subscriptionsUrl: urlManager.initiativeSubscribers({
            orgId,
            initiativeId: this.entity.id
          }),
          detailsUrl: urlManager.initiativeDetail({ orgId, initiativeId: this.entityId })
        }
      }
    }
  },
  methods: {
    async toggleSubscription() {
      let action = 'subscribe'
      if (this.isUserSubscribed) {
        action = 'unsubscribe'
      }
      try {
        this.isSubscribing = true
        const url = this.entityMap[this.entityType]?.detailsUrl
        await service.client.post(url, { action })
        this.eventBusEmit('ADD_TOAST', {
          message:
            'You have ' + (action === 'subscribe' ? 'subscribed' : 'unsubscribed') + ' successfully'
        })
        this.eventBusEmit(action === 'subscribe' ? events.SUBSCRIBED : events.UNSUBSCRIBED, {
          userId: this.user.id,
          entityType: this.entityType,
          entityId: this.entityId
        })
      } catch (e) {
        showAPIError(e)
      }
      this.isSubscribing = false
    },
    async refreshSubscribers() {
      try {
        const url = this.entityMap[this.entityType]?.subscriptionsUrl
        const response = await service.client.get(url)
        this.subscribers = response.data
      } catch (e) {
        showAPIError(e)
      }
    },
    onSubscribe(userId) {
      this.subscribers = [...this.subscribers, userId]
    },
    onUnsubscribe(userId) {
      this.subscribers = [...this.subscribers, userId]
      this.subscribers = R.without([userId], this.subscribers)
    }
  },
  mounted() {
    this.refreshSubscribers()
    this.listenToEventBus(events.SUBSCRIBED, ({ entityType, entityId, userId }) => {
      if (entityType === this.entityType && entityId === this.entityId) {
        this.onSubscribe(userId)
      }
    })
    this.listenToEventBus(events.UNSUBSCRIBED, ({ entityType, entityId, userId }) => {
      if (entityType === this.entityType && entityId === this.entityId) {
        this.onUnsubscribe(userId)
      }
    })
  },
  watch: {
    entity(current, previous) {
      if (previous && current.id === previous.id) {
        const ownershipChanged = !R.equals(
          [current.ownerId, ...current.coOwnerIds],
          [previous.ownerId, ...previous.coOwnerIds]
        )
        if (ownershipChanged) {
          this.refreshSubscribers()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.subscribers-container {
  max-width: 208px;
}

.subscribe-btn {
  padding: 2px 12px;
}
</style>
